import { useState, useRef, useCallback } from 'react'
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { uploadReferenceImage } from '../../lib/api'

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const MAX_SIZE_BYTES = 10 * 1024 * 1024 // 10 MB

interface UploadResult {
  image_id: string
  public_url: string
}

interface ReferenceImageUploaderProps {
  onUploadComplete: (result: UploadResult) => void
  onClear: () => void
  currentImageUrl?: string | null
}

export function ReferenceImageUploader({
  onUploadComplete,
  onClear,
  currentImageUrl,
}: ReferenceImageUploaderProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl ?? null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Solo se aceptan archivos PNG, JPG o WebP'
    }
    if (file.size > MAX_SIZE_BYTES) {
      return 'El archivo no puede superar 10 MB'
    }
    return null
  }

  const handleFile = useCallback(
    async (file: File) => {
      const error = validateFile(file)
      if (error) {
        toast.error(error)
        return
      }

      const localPreview = URL.createObjectURL(file)
      setPreviewUrl(localPreview)
      setIsUploading(true)

      try {
        const result = await uploadReferenceImage(file)
        onUploadComplete(result)
        toast.success('Imagen de referencia cargada')
      } catch (err) {
        setPreviewUrl(null)
        toast.error('Error al subir la imagen', {
          description: err instanceof Error ? err.message : 'Intenta de nuevo',
        })
      } finally {
        setIsUploading(false)
        URL.revokeObjectURL(localPreview)
      }
    },
    [onUploadComplete]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragging(false)
      const file = e.dataTransfer.files[0]
      if (file) handleFile(file)
    },
    [handleFile]
  )

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => setIsDragging(false)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    e.target.value = ''
  }

  const handleClear = () => {
    setPreviewUrl(null)
    onClear()
  }

  if (previewUrl) {
    return (
      <div className="relative inline-flex items-start gap-3">
        <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 flex-shrink-0">
          <img
            src={previewUrl}
            alt="Referencia"
            className="w-full h-full object-cover"
          />
          {isUploading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 pt-1">
          <p className="text-xs font-medium text-slate-700">
            {isUploading ? 'Subiendo...' : 'Imagen cargada'}
          </p>
          <p className="text-[11px] text-slate-500">
            Se usara como base para la generacion
          </p>
          {!isUploading && (
            <button
              type="button"
              onClick={handleClear}
              className="mt-1 flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-medium"
            >
              <X className="w-3.5 h-3.5" />
              Quitar imagen
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-lg border-2 border-dashed
          cursor-pointer transition-colors select-none
          ${isDragging
            ? 'border-emerald-400 bg-emerald-50'
            : 'border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100'}
        `}
      >
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-slate-200 text-slate-400">
          <ImageIcon className="w-5 h-5" />
        </span>
        <div className="text-center">
          <p className="text-sm font-medium text-slate-700 flex items-center gap-1.5 justify-center">
            <Upload className="w-3.5 h-3.5" />
            Arrastra o haz clic para subir
          </p>
          <p className="text-xs text-slate-500 mt-0.5">PNG, JPG o WebP — max 10 MB</p>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        className="hidden"
        onChange={handleInputChange}
      />
    </>
  )
}
